// 写作三步流程（需求配置 → 确认大纲 → 预览全文）共用的工具函数与常量。
// 参照墨得 xiaomo-mwt 的 StepForm（referenceLimits / dataSearchSelection / AnalyticCitationUtil 等）移植。
import {
    CONTENT_TO_EXTRA,
    COURSE_PAPER_WORD_COUNT,
    COURSE_PAPER_WORD_COUNT_OPTIONS,
    DEFAULT_WORD_COUNT,
    QUERY_TYPE,
    QUERY_TYPE_LABEL,
    SPECIAL_WORD_COUNT,
    WORD_COUNT_OPTIONS,
} from '../api';

// ==================== 通用 ====================

/**
 * axios 拦截器只在后端返回了 msg 时弹出有效提示；其余情况（超时、断网、code≠0）由调用方提示。
 */
export const isErrorNotified = (err) => Boolean(err?.response?.data?.msg) && !err?.config?.slient;

export const errorText = (err, fallback) => {
    if (err?.response) return err.response.data?.msg || fallback;
    if (err?.isAxiosError) return err.code === 'ECONNABORTED' ? '请求超时，请稍后重试' : fallback;
    return err?.message || fallback;
};

// ==================== 写作类型 ====================

/** 开题报告 / 文献综述：默认 8000 字、默认不嵌入图表，大纲是没有节的模块章 */
export const isSpecialQueryType = (type) =>
    type === QUERY_TYPE.OPENING_REPORT || type === QUERY_TYPE.LITERATURE_REVIEW;

/** 课程长文：按短篇长文处理（默认包含图表，篇幅 3000~15000 字） */
export const isCoursePaper = (type) => type === QUERY_TYPE.COURSE_PAPER;

export const normalizeQueryType = (type) =>
    Object.values(QUERY_TYPE).includes(type) ? type : QUERY_TYPE.PAPER;

/** 某写作类型的默认篇幅 */
export const defaultWordCountOf = (type) => {
    if (isCoursePaper(type)) return COURSE_PAPER_WORD_COUNT;
    return isSpecialQueryType(type) ? SPECIAL_WORD_COUNT : DEFAULT_WORD_COUNT;
};

/** 某写作类型可选的篇幅 */
export const wordCountOptionsOf = (type) =>
    isCoursePaper(type) ? COURSE_PAPER_WORD_COUNT_OPTIONS : WORD_COUNT_OPTIONS;

/** 篇幅不在该类型的可选范围内时回落到默认值 */
export const clampWordCount = (type, count) =>
    wordCountOptionsOf(type).includes(count) ? count : defaultWordCountOf(type);

/** 需求配置里「写作类型」的选项（顺序与首页 Tab 一致） */
export const WRITING_TYPE_OPTIONS = [
    QUERY_TYPE.PAPER,
    QUERY_TYPE.OPENING_REPORT,
    QUERY_TYPE.LITERATURE_REVIEW,
    QUERY_TYPE.COURSE_PAPER,
].map((value) => ({ value, label: QUERY_TYPE_LABEL[value] }));

export const WRITING_TYPE_NOTES = {
    [QUERY_TYPE.PAPER]: '按完整长文结构生成正文，含中英文摘要、结论与致谢',
    [QUERY_TYPE.COURSE_PAPER]: '按课程长文结构组织：引言、正文论述与结论，篇幅精炼',
    [QUERY_TYPE.OPENING_REPORT]: '按开题结构组织：研究背景、问题、方法与技术路线',
    [QUERY_TYPE.LITERATURE_REVIEW]: '按综述结构组织：研究现状、评述与研究空白',
};

/** 毕业长文默认包含的元素 */
export const DEFAULT_CONTENTS = ['image', 'table'];

export const formatWordCount = (count) =>
    count >= 10000 ? `${count / 10000}万字` : `${count}字`;

/** 界面元素 key（image/table/...）→ 后端 autoLightTypes 字符串 */
export const contentsToLightTypes = (contents) =>
    (contents || [])
        .map((key) => CONTENT_TO_EXTRA[key])
        .filter(Boolean)
        .join(',');

// ==================== 参考资料 ====================

/** 最终选用（提交给后端）的参考资料上限 */
export const MAX_REFERENCE_COUNT = 35;
/** 自动补充外文文献最多补几篇 */
export const MAX_ENGLISH_SUPPLEMENT = 5;
/** 自定义添加（粘贴 / Excel / RIS）的候选列表上限 */
export const MAX_CUSTOM_REFERENCE_COUNT = 200;
/** 上传文件大小上限（MB） */
export const MAX_UPLOAD_MB = 50;

export const REFERENCE_TYPE = {
    SEARCH_RESULT: 'SEARCH_RESULT', // AI 检索
    MANUAL_ADD: 'MANUAL_ADD', // 手动粘贴
    EXCEL_UPLOAD: 'EXCEL_UPLOAD', // Excel 上传
    RIS_UPLOAD: 'RIS_UPLOAD', // RIS 上传
};

/** 自定义添加的三种来源 */
export const CUSTOM_SOURCE_TYPES = new Set([
    REFERENCE_TYPE.MANUAL_ADD,
    REFERENCE_TYPE.EXCEL_UPLOAD,
    REFERENCE_TYPE.RIS_UPLOAD,
]);

export const REFERENCE_SOURCE_CONFIG = {
    [REFERENCE_TYPE.SEARCH_RESULT]: {
        label: 'AI检索',
        shortLabel: '检索',
        color: '#1677ff',
        bgColor: '#e6f4ff',
        borderColor: '#91caff',
    },
    [REFERENCE_TYPE.MANUAL_ADD]: {
        label: '手动添加',
        shortLabel: '手动',
        color: '#389e0d',
        bgColor: '#f6ffed',
        borderColor: '#b7eb8f',
    },
    [REFERENCE_TYPE.EXCEL_UPLOAD]: {
        label: 'Excel上传',
        shortLabel: 'Excel',
        color: '#531dab',
        bgColor: '#f9f0ff',
        borderColor: '#d3adf7',
    },
    [REFERENCE_TYPE.RIS_UPLOAD]: {
        label: 'RIS上传',
        shortLabel: 'RIS',
        color: '#08979c',
        bgColor: '#e6fffb',
        borderColor: '#87e8de',
    },
};

export const getSourceConfig = (sourceType) => REFERENCE_SOURCE_CONFIG[sourceType] || null;

/** 题录去重键：同一条文本再粘贴一次 id 会变，只能按文本判重 */
export const refDedupeKey = (ref) =>
    String(ref?.references ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

const hashText = (text) => {
    let hash = 5381;
    for (let i = 0; i < text.length; i += 1) {
        hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
};

/**
 * 检索结果的稳定 id：同一题录重新检索后 id 不变，已勾选状态能对上。
 * seen 用来给同一批结果里重复的题录加序号。
 */
export const stableReferenceId = (type, text, seen) => {
    const base = `${type}_${hashText(refDedupeKey({ references: text }))}`;
    const count = seen ? seen.get(base) || 0 : 0;
    if (seen) seen.set(base, count + 1);
    return count > 0 ? `${base}_${count}` : base;
};

/** 自定义添加的 id：带时间戳，保证每次添加唯一 */
export const generateReferenceId = (type, title, index = 0) => {
    const clean = String(title || '')
        .replace(/\s+/g, '')
        .replace(/[^一-龥a-zA-Z0-9_]/g, '')
        .substring(0, 30);
    const base = `${type}_${clean || 'doc'}_${Date.now()}`;
    return index > 0 ? `${base}_${index}` : base;
};

/** 题录后面的说明段落标签：摘要 / 成果简介 等，从这行起都算摘要（说明），不进题录 */
const ABSTRACT_LABEL = /^(摘要|成果简介|内容简介|项目简介|简介|Abstract)\s*[:：]\s*/i;

/**
 * 续行是不是说明文字而不是题录的折行：题录里不会出现句号、分号这类整句标点，
 * 「已申请软件著作权」「发表时间」「关键词」这类开头也是查新报告里的说明项。
 */
const PROSE_LINE = /[。！？；]|^(已申请|发表时间|关键词|Keywords|备注|来源|被引|下载量)/i;

const normalizeCite = (cite) => String(cite || '').replace(/\s+/g, ' ').trim();

/**
 * 解析粘贴的参考文献（查新引文格式，[1] [2] 编号开头，可带「摘要：」「成果简介：」）。
 * 一条文献只有第一行（和真正折行的续行）算题录；后面的摘要、成果简介、
 * 已申请软件著作权 / 发表时间之类的说明都归到 abstractContent。没有编号时按行解析。
 */
export function parseReferences(text) {
    const result = [];
    const cleanText = String(text || '')
        .replace(/\r\n?/g, '\n')
        .trim();
    const entries = cleanText.split(/(?=\[\d+\])/);

    for (const entry of entries) {
        const trimmed = entry.trim();
        if (!trimmed || !/^\[\d+\]/.test(trimmed)) continue;

        const lines = trimmed
            .replace(/^\[\d+\]\s*/, '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
        let cite = lines.length ? lines[0] : '';
        const abstractParts = [];
        for (const line of lines.slice(1)) {
            const label = line.match(ABSTRACT_LABEL);
            if (label) {
                abstractParts.push(line.slice(label[0].length).trim());
            } else if (abstractParts.length > 0 || PROSE_LINE.test(line)) {
                abstractParts.push(line);
            } else {
                cite += ` ${line}`;
            }
        }

        // 摘要写在同一行里（「…[J]. 摘要：…」）的老格式
        const inline = cite.match(/摘要[:：]/);
        if (inline) {
            abstractParts.unshift(cite.slice(inline.index + inline[0].length).trim());
            cite = cite.slice(0, inline.index);
        }

        result.push({
            references: normalizeCite(cite),
            abstractContent: abstractParts.filter(Boolean).join(' ').trim(),
        });
    }

    if (result.length === 0 && cleanText.length > 0) {
        cleanText
            .split(/\n/)
            .map((line) => line.trim())
            .filter((line) => line && /[a-zA-Z一-龥]/.test(line) && line.includes('.'))
            .forEach((line) => result.push({ references: line, abstractContent: '' }));
    }

    return result.filter((ref) => ref.references);
}

/** 1 中文 / 2 外文：汉字占比超过 15% 视为中文 */
export const detectLanguage = (text) => {
    if (!text) return 1;
    const chinese = (text.match(/[一-龥]/g) || []).length;
    return chinese / text.length > 0.15 ? 1 : 2;
};

/** 已选参考资料 → 下单 customRef：AI 检索的进 abstracts，用户添加 / 上传的进 customAbstracts */
export const buildCustomRef = (references) => {
    if (!Array.isArray(references) || references.length === 0) return undefined;
    const abstracts = [];
    const customAbstracts = [];
    references.forEach((ref, index) => {
        const item = {
            serialNo: index + 1,
            references: ref.references || '',
            abstractContent: ref.abstractContent || '',
            language: detectLanguage(ref.references),
        };
        if (ref.sourceType === REFERENCE_TYPE.SEARCH_RESULT) abstracts.push(item);
        else customAbstracts.push(item);
    });
    return { abstracts, customAbstracts };
};

// ==================== 大纲 ====================

/** 界面 key ↔ 后端 selected_extra */
export const UI_TO_API_KEY = {
    image: 'DIAGRAM',
    table: 'TABLE',
    code: 'CODE',
    formula: 'FORMULA',
    webSearch: 'DATA',
};

export const API_TO_UI_KEY = {
    DIAGRAM: 'image',
    TABLE: 'table',
    CODE: 'code',
    FORMULA: 'formula',
    DATA: 'webSearch',
};

export const buildSelectedExtra = (extras) =>
    (extras || [])
        .map((key) => UI_TO_API_KEY[key])
        .filter(Boolean)
        .join(',');

const parseSelectedExtra = (selectedExtra) =>
    selectedExtra
        ? [
              ...new Set(
                  String(selectedExtra)
                      .split(',')
                      .map((key) => API_TO_UI_KEY[key.trim()])
                      .filter(Boolean),
              ),
          ]
        : [];

const parseExtraDesc = (extraTypeCn) => {
    const desc = {};
    Object.entries(extraTypeCn || {}).forEach(([apiKey, value]) => {
        const uiKey = API_TO_UI_KEY[apiKey];
        if (uiKey) desc[uiKey] = value;
    });
    return desc;
};

const apiTypes = (node) =>
    typeof node?.selected_extra === 'string'
        ? node.selected_extra
              .split(',')
              .map((type) => type.trim())
              .filter(Boolean)
        : [];

/** 编辑器里的选择优先；还没解析过的接口节点以 selected_extra 为准 */
export const isDataSearchSelected = (node) =>
    Array.isArray(node?.extras) ? node.extras.includes('webSearch') : apiTypes(node).includes('DATA');

/** 去掉已经没有用户选择支撑的联网数据字段（全篇取消「数据」时 clearSelected） */
export function normalizeOutlineDataSearch(outline, { clearSelected = false } = {}) {
    if (!Array.isArray(outline)) return outline;
    return outline.map((node) => {
        if (!node || typeof node !== 'object') return node;
        const next = { ...node };
        const selected = !clearSelected && isDataSearchSelected(node);
        const types = apiTypes(node);
        if (selected && !types.includes('DATA')) {
            next.selected_extra = [...types, 'DATA'].join(',');
        } else if (!selected) {
            if (types.includes('DATA')) {
                next.selected_extra = types.filter((type) => type !== 'DATA').join(',');
            }
            if (Array.isArray(node.extras) && node.extras.includes('webSearch')) {
                next.extras = node.extras.filter((type) => type !== 'webSearch');
            }
            [
                ['extraTypeCn', 'DATA'],
                ['extraDesc', 'webSearch'],
            ].forEach(([field, key]) => {
                if (node[field] && Object.prototype.hasOwnProperty.call(node[field], key)) {
                    next[field] = { ...node[field] };
                    delete next[field][key];
                }
            });
        }
        ['sections', 'subsections'].forEach((childKey) => {
            if (Array.isArray(node[childKey])) {
                next[childKey] = normalizeOutlineDataSearch(node[childKey], { clearSelected });
            }
        });
        return next;
    });
}

export const hasSelectedDataSearch = (outline) =>
    Array.isArray(outline) &&
    outline.some(
        (node) =>
            node &&
            (isDataSearchSelected(node) ||
                hasSelectedDataSearch(node.sections) ||
                hasSelectedDataSearch(node.subsections)),
    );

/** 下单的 autoLightTypes：DATA 只在确认后的大纲里真有节选了联网数据时才带 */
export const dataSearchLightTypes = (types, outline) =>
    [
        ...String(types || '')
            .split(',')
            .filter((type) => type && type !== 'DATA'),
        ...(hasSelectedDataSearch(outline) ? ['DATA'] : []),
    ].join(',');

/** 接口返回的大纲 → 编辑器数据：补唯一 id，解析 selected_extra / extraTypeCn */
export const toEditableOutline = (rawOutlines, prefix = 'gen') =>
    (normalizeOutlineDataSearch(Array.isArray(rawOutlines) ? rawOutlines : []) || []).map(
        (ch, idx) => ({
            ...ch,
            id: ch.id || `${prefix}_ch_${idx + 1}`,
            extras: parseSelectedExtra(ch.selected_extra),
            extraDesc: parseExtraDesc(ch.extraTypeCn),
            sections: (ch.sections || []).map((sec, secIdx) => ({
                ...sec,
                id: sec.id || `${prefix}_sec_${idx + 1}_${secIdx + 1}`,
                extras: parseSelectedExtra(sec.selected_extra),
                extraDesc: parseExtraDesc(sec.extraTypeCn),
                subsections: (sec.subsections || []).map((sub, subIdx) => ({
                    ...sub,
                    id: sub.id || `${prefix}_sub_${idx + 1}_${secIdx + 1}_${subIdx + 1}`,
                    extras: parseSelectedExtra(sub.selected_extra),
                    extraDesc: parseExtraDesc(sub.extraTypeCn),
                })),
            })),
        }),
    );

const UI_ONLY_FIELDS = ['id', 'extras', 'extraDesc', 'dataSearchKeywords'];

const stripNode = (node) => {
    const next = {};
    Object.keys(node || {}).forEach((key) => {
        if (!UI_ONLY_FIELDS.includes(key)) next[key] = node[key];
    });
    if (!next.selected_extra) next.selected_extra = '';
    if (Array.isArray(node?.sections)) next.sections = node.sections.map(stripNode);
    if (Array.isArray(node?.subsections)) next.subsections = node.subsections.map(stripNode);
    return next;
};

/** 编辑器数据 → 下单 outlines（契约 OutlineNode 结构，去掉界面专用字段） */
export const toSubmitOutlines = (outline) =>
    (normalizeOutlineDataSearch(outline) || []).map(stripNode);
